import { useState } from 'react';

import useSWRInfinite from 'swr/infinite';

// Utils
import { get } from '@/utils/client';

// API Endpoints
import { GROUP_STORIES, STORIES } from '@/lib/routes/apiEndpoints';

/**
 * Check if we have at least one valid ID to make an API request
 * @returns {boolean} True if at least one ID is valid
 */
const hasValidID = (groupID, activeSPID) => {
    return [groupID, activeSPID].some((id) => id != null && id !== undefined);
};

const getKey = (
    pageIndex,
    previousPageData,
    activeSPID,
    limit,
    requestParams,
    endpoint = STORIES,
    groupID
) => {
    // No request params
    if (!requestParams) return null;

    // Validate that we have at least one valid ID to make the request
    if (!hasValidID(groupID, activeSPID)) return null;

    // Reached the end
    if (previousPageData && !previousPageData.stories) return null;

    // Determine the endpoint based on whether we're fetching for a group or profile
    let finalEndpoint;
    let finalRequestParams = requestParams;

    if (groupID && endpoint === STORIES) {
        finalEndpoint = GROUP_STORIES.replace('%GROUP_ID%', groupID);
    } else if (activeSPID) {
        finalEndpoint = endpoint.replace('%SPID%', activeSPID);
    } else {
        return null;
    }

    // First page, we don't have `previousPageData`
    if (pageIndex === 0) {
        return [`${finalEndpoint}?per_page=${limit}`, JSON.stringify(finalRequestParams)];
    }

    // Pagination for groups (next_page_url)
    if (previousPageData.next_page_url) {
        return [
            `${finalEndpoint}?per_page=${limit}&page=${previousPageData.current_page + 1}`,
            JSON.stringify(finalRequestParams)
        ];
    }

    // Pagination for profiles (cursor)
    return [
        `${finalEndpoint}?per_page=${limit}&cursor=${previousPageData.cursor}`,
        JSON.stringify(finalRequestParams)
    ];
};

const useStories = ({
    activeSPID = null,
    groupID = null,
    limit = 20,
    params = null,
    options = {},
    endpoint = undefined
} = {}) => {
    const [requestParams, setRequestParams] = useState(params);

    const {
        data: dataStories,
        size,
        setSize,
        mutate: mutateStories,
        error: storiesError
    } = useSWRInfinite(
        (pageIndex, previousPageData) =>
            getKey(
                pageIndex,
                previousPageData,
                activeSPID,
                limit,
                requestParams,
                endpoint,
                groupID
            ),
        ([url, params]) => get(url, JSON.parse(params)).then((res) => res.data),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );

    // Only show loading if we have valid IDs and are actually fetching
    // If we don't have valid IDs, we're not loading (no request will be made)
    const isLoadingInitialData = hasValidID(groupID, activeSPID) && !dataStories && !storiesError;
    const isLoadingMore = size > 0 && dataStories && typeof dataStories[size - 1] === 'undefined';

    return {
        dataStories,
        loadMoreStories: () => setSize(size + 1), // Function to load more stories (load more button)
        mutateStories,
        storiesError,
        requestParams,
        setRequestParams, // Setter to change parameters in the url
        isLoadingInitialData, // Check if it's the initial loading (not the load more)
        isLoadMore:
            dataStories &&
            (typeof dataStories[dataStories.length - 1].cursor === 'string' ||
                typeof dataStories[dataStories.length - 1].next_page_url === 'string'), // Check if there is a cursor or next_page_url to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};

export default useStories;
